import React, { useCallback, useEffect, useState } from 'react';
import http from '../../services/httpClient';
import { postUndo } from '../../utils/ccsUndo';
import '../banquet/banquet.css';
import './outletSale.css';

const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const dayLabel = (iso) => {
  const [y, m, d] = String(iso || '').slice(0, 10).split('-').map(Number);
  return y ? new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
};

/**
 * Revert an outlet sale so it can be filed again. An outlet's sale is an
 * ordinary TSR sale carrying its outlet, so this is the normal TSR revert; the
 * server is the gate (revert window, count in progress). NC plates filed with
 * it through the TSR screen are separate wastage records, reverted as such.
 */
export default function OutletRevertModal({ open, onClose, onReverted }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [asking, setAsking] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await http.get('/tsr/outlet-sales/recent');
      const data = (res && (res.data?.data || res.data)) || [];
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.data?.error || err.message || 'Could not load outlet sales');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);
  if (!open) return null;

  const revert = async (row) => {
    setBusy(true); setError(null);
    try {
      const res = await postUndo(`/tsr/revert/${row.sale_id}`, {});
      // httpClient answers a 403 with {success:false} instead of throwing.
      if (!res || res.success === false) {
        throw Object.assign(new Error((res && (res.error || res.message)) || 'Revert failed'), { data: res });
      }
      setAsking(null);
      if (onReverted) onReverted(row);
      await load();
    } catch (err) {
      setError(err?.data?.error || err?.data?.message || err.message || 'Revert failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bq-modal-overlay" onClick={onClose}>
      <div className="bq-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Revert Outlet Sale">
        <h3>Revert Outlet Sale</h3>
        {loading && <p>Loading…</p>}
        {!loading && rows.length === 0 && <p>No outlet sales can be reverted.</p>}
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {rows.map((r) => (
            <li key={r.sale_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
              <span>{dayLabel(r.date)} · {r.outlet} · {inr(r.total_amount)}</span>
              {asking === r.sale_id ? (
                <span style={{ display: 'flex', gap: 6 }}>
                  <button type="button" onClick={() => setAsking(null)} disabled={busy}>Cancel</button>
                  <button type="button" onClick={() => revert(r)} disabled={busy}>Yes, revert {r.outlet}</button>
                </span>
              ) : (
                <button type="button" onClick={() => setAsking(r.sale_id)}>Revert</button>
              )}
            </li>
          ))}
        </ul>
        {error && <div className="os-banner os-banner--error" role="alert">{error}</div>}
        <button type="button" onClick={onClose} style={{ marginTop: 10 }}>Close</button>
      </div>
    </div>
  );
}
